import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {reduxForm} from 'redux-form';
// @ts-expect-error
import type {FormProps} from 'redux-form/lib/types.js.flow';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import type {GraphQLErrorObject} from 'nutshell-core/utils/graphql-errors';

import {SaveButton} from 'shells/button/save-button/save-button';
import {DisabledSection} from 'shells/disabled-section/disabled-section';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {ListSequence} from 'shells/list-sequence/list-sequence';
import {ListSequenceRowBody} from 'shells/list-sequence/list-sequence-row-body';
import {FooterBar} from 'shells/modal/footer-bar';

import type {QueuedEmail as QueuedEmailFragment} from 'nutshell-graphql-types';

import type {EmailComposerFormState} from '../../sender/types';

import {FORM_NAME} from './constants';
import {EmailComposerForm} from './email-composer-form';

import styles from './scheduled-email-edit-form.module.css';

type OwnProps = {
    onSubmit: (arg1: EmailComposerFormState) => Promise<any>;
    closeModal: () => void;
    queuedEmails: QueuedEmailFragment[];
    editingEmailId: string;
    templateListErrorMessage: string | null | undefined;
};

type Props = OwnProps & FormProps;

type State = {
    selectedEmailId: string;
    emailsById: {
        [key: string]: QueuedEmailFragment;
    };
    saving: boolean;
    error?: string | null | undefined;
};

class ScheduledEmailEditFormConnected extends React.PureComponent<Props, State> {
    saveButton: HTMLElement | null | undefined;

    constructor(props: Props) {
        super(props);
        this.state = {
            saving: false,
            selectedEmailId: props.editingEmailId,
            // @ts-expect-error
            emailsById: props.queuedEmails.reduce<Record<string, any>>((accum, email) => {
                accum[email.id] = email;

                return accum;
            }, {}),
        };
    }

    componentDidMount() {
        ReactTooltip.rebuild();
    }

    componentWillUnmount() {
        ReactTooltip.hide();
    }

    render() {
        return (
            <form className={styles.container}>
                {this.state.error ? (
                    <FlashBanner type='error' onHidden={() => this.setState({error: undefined})}>
                        {this.state.error}
                    </FlashBanner>
                ) : undefined}
                {this.props.templateListErrorMessage ? (
                    <FlashBanner type='error' onHidden={() => this.setState({error: undefined})}>
                        {this.props.templateListErrorMessage}
                    </FlashBanner>
                ) : undefined}
                <div className={styles['container-horizontal']}>
                    <div className={styles['form-body']}>
                        <div className={styles['list-container']}>
                            <ListSequence
                                shouldShowCounts={false}
                                collection={this.props.queuedEmails}
                                selectedRowId={this.state.selectedEmailId}
                                renderRow={this.renderRow}
                                // This prop isn't actually needed, but it
                                // forces a re-render when the state updates
                                // @ts-expect-error
                                extraState={this.state.emailsById}
                            />
                        </div>
                        <div className={styles['form-container']}>
                            <EmailComposerForm
                                editingEmailId={this.state.selectedEmailId}
                                queuedEmails={this.props.queuedEmails}
                                isMultiSequence={true}
                                onChangePlaintextBody={this.handleChangePlaintextBody}
                                onChangeSubject={this.handleChangeSubject}
                            />
                        </div>
                    </div>
                </div>
                <FooterBar
                    right={
                        <SaveButton
                            // We want to use the saveButton, but don't want it to actually submit,
                            // because we have some confirmation logic that needs to run first.
                            // @ts-expect-error
                            type='button'
                            // @ts-expect-error
                            getButtonRef={(node) => {
                                this.saveButton = node;
                            }}
                            onClick={this.handleSaveClick}
                            onClearSaveComplete={this.handleClearSaveComplete}
                            errorSaving={Boolean(this.state.error)}
                            isSaving={this.state.saving}
                        />
                    }
                />
            </form>
        );
    }

    renderRow = (rowProps: {
        isSelected: boolean;
        shouldShowCounts: boolean;
        row: QueuedEmailFragment;
        index: number;
    }) => {
        const {isSelected, shouldShowCounts, row: template} = rowProps;
        const emailId = template.id;
        const emailState = this.state.emailsById[emailId];

        const body = (
            <ListSequenceRowBody
                isSelected={isSelected}
                onClick={() => {
                    this.setState({selectedEmailId: emailId});
                }}
                // @ts-expect-error
                title={emailState.subject}
                shouldShowCounts={shouldShowCounts}
                // Have to trim the bodyText, because quill adds a carriage return
                // @ts-expect-error
                subtitle={emailState.bodyText.trim()}
            />
        );

        if (template.status === 'SCHEDULED') {
            return {
                body: (
                    <div key={emailId} className={styles['email-template-list-item']}>
                        {body}
                    </div>
                ),
            };
        } else {
            return {
                body: (
                    <div
                        data-tip='Already sent'
                        data-place='right'
                        className={styles['email-template-list-item--disabled']}
                    >
                        <DisabledSection>{body}</DisabledSection>
                    </div>
                ),
            };
        }
    };

    handleClearSaveComplete = () => {
        this.props.closeModal();
    };

    handleChangePlaintextBody = (plaintextBody: string, id: string) => {
        // @ts-expect-error
        this.setState((prevState) => {
            return {
                emailsById: {
                    ...prevState.emailsById,
                    [id]: {
                        ...prevState.emailsById[id],
                        bodyText: plaintextBody,
                    },
                },
            };
        });
    };

    handleChangeSubject = (newSubject: string, id: string) => {
        this.changeEmailSubject(newSubject, id);
        // @ts-expect-error
        let emailIndex = this.props.queuedEmails.findIndex((email) => {
            return email.id === id;
        });

        if (emailIndex < 0) {
            // This email doesnt exist!
            return;
        }

        emailIndex++;

        for (; emailIndex < this.props.queuedEmails.length; emailIndex++) {
            if (!this.props.queuedEmails[emailIndex].sendAsReply) {
                // This email is not a reply, don't update the subject
                return;
            }
            this.changeEmailSubject(`Re: ${newSubject}`, this.props.queuedEmails[emailIndex].id);
        }
    };

    changeEmailSubject = (newSubject: string, id: string) => {
        // @ts-expect-error
        this.setState((prevState) => {
            return {
                emailsById: {
                    ...prevState.emailsById,
                    [id]: {
                        ...prevState.emailsById[id],
                        subject: newSubject,
                    },
                },
            };
        });
    };

    handleSaveClick = () => {
        return this.props.handleSubmit(this.handleSubmit)();
    };

    handleSubmit = (values: EmailComposerFormState) => {
        this.setState({saving: true});

        return this.props
            .onSubmit(values)
            .then(() => {
                this.setState({saving: false});
                // Pass the email sequence id
                const sequenceId =
                    this.props.queuedEmails.length && this.props.queuedEmails[0].emailSequence
                        ? this.props.queuedEmails[0].emailSequence.id
                        : undefined;

                window.dispatchEvent(
                    new CustomEvent('refreshScheduledEmails', {detail: sequenceId})
                );
            })
            .catch((error: GraphQLErrorObject) => {
                this.setState({saving: false});
                const errorMessage = safelyGetGraphQLErrorMessage(error);
                this.setState({error: errorMessage});
            });
    };
}

function validate(values: EmailComposerFormState) {
    const errors: Record<string, any> = {};

    if (!values.toAddresses || !values.toAddresses.length) {
        // No recipent address
        errors.toAddresses = 'Who should this be sent to?';
    }

    return errors;
}

const withReduxForm = reduxForm({
    form: FORM_NAME,
    validate,
});

export const ScheduledEmailEditForm = withReduxForm(ScheduledEmailEditFormConnected);
